import React from "react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Cell, PieChart, Pie, Legend,
} from "recharts";

const VIOLET = "#7C3AED";
const EMERALD = "#10B981";
const PALETTE = ["#7C3AED", "#0EA5E9", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#14B8A6", "#F97316"];

const fmtDate = (d) => {
  const [, m, day] = String(d).split("-");
  return `${day}/${m}`;
};

function ChartTooltip({ active, payload, label, labelFormatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-slate-900 text-white px-3 py-2 text-[11px] shadow-xl border border-slate-700">
      {label !== undefined && <div className="font-semibold mb-1">{labelFormatter ? labelFormatter(label) : label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-slate-300">{p.name} :</span>
          <span className="font-bold tabular-nums">{p.value}{p.unit || ""}</span>
        </div>
      ))}
    </div>
  );
}

export function ChartCard({ title, subtitle, children, testid, className = "" }) {
  return (
    <div className={`rounded-2xl bg-white border border-slate-200 p-5 ${className}`} data-testid={testid}>
      <div className="font-heading text-sm font-semibold text-slate-900">{title}</div>
      {subtitle && <div className="text-[11px] text-slate-400 mt-0.5">{subtitle}</div>}
      <div className="mt-4">{children}</div>
    </div>
  );
}

export function ActivityChart({ series }) {
  return (
    <ResponsiveContainer width="100%" height={280}>
      <ComposedChart data={series} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
        <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
        <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} allowDecimals={false} />
        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} unit="%" width={45} />
        <Tooltip content={<ChartTooltip labelFormatter={fmtDate} />} cursor={{ fill: "rgba(124,58,237,0.05)" }} />
        <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
        <Bar yAxisId="left" dataKey="reservations" name="Réservations" fill={VIOLET} radius={[4, 4, 0, 0]} maxBarSize={26} />
        <Bar yAxisId="left" dataKey="annulations" name="Annulations" fill="#FCA5A5" radius={[4, 4, 0, 0]} maxBarSize={26} />
        <Line yAxisId="right" dataKey="occupation" name="Occupation" unit="%" stroke={EMERALD} strokeWidth={2.5} dot={false} type="monotone" />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function TopVehiclesChart({ data, onVehicleClick }) {
  return (
    <ResponsiveContainer width="100%" height={Math.max(180, data.length * 38)}>
      <BarChart data={data} layout="vertical" margin={{ top: 0, right: 30, left: 10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
        <XAxis type="number" unit="%" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} domain={[0, 100]} />
        <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 10, fill: "#475569" }} axisLine={false} tickLine={false} />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(124,58,237,0.05)" }} />
        <Bar dataKey="occupancy" name="Occupation" unit="%" radius={[0, 6, 6, 0]} maxBarSize={18} onClick={(d) => onVehicleClick?.(d.id)} className="cursor-pointer">
          {data.map((v, i) => (
            <Cell key={v.id} fill={v.occupancy >= 60 ? EMERALD : v.occupancy >= 25 ? VIOLET : "#CBD5E1"} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function CategoryChart({ data }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
        <XAxis dataKey="category" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(124,58,237,0.05)" }} />
        <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
        <Bar dataKey="bookings" name="Réservations" fill={VIOLET} radius={[4, 4, 0, 0]} maxBarSize={34} />
        <Bar dataKey="occupancy" name="Occupation" unit="%" fill={EMERALD} radius={[4, 4, 0, 0]} maxBarSize={34} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function OptionsDonut({ data }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie data={data} dataKey="count" nameKey="name" innerRadius={52} outerRadius={80} paddingAngle={3} strokeWidth={0}>
          {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
        </Pie>
        <Tooltip content={<ChartTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} layout="vertical" align="right" verticalAlign="middle" />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function LocationsChart({ data }) {
  return (
    <ResponsiveContainer width="100%" height={Math.max(160, data.length * 40)}>
      <BarChart data={data} layout="vertical" margin={{ top: 0, right: 30, left: 10, bottom: 0 }}>
        <XAxis type="number" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} allowDecimals={false} />
        <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10, fill: "#475569" }} axisLine={false} tickLine={false} />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(14,165,233,0.05)" }} />
        <Bar dataKey="bookings" name="Réservations" fill="#0EA5E9" radius={[0, 6, 6, 0]} maxBarSize={18} />
      </BarChart>
    </ResponsiveContainer>
  );
}
